/**
 * MidJourneySpawnModal — DRR-309 Phase 6
 *
 * Modal for spawning related journeys mid-workflow.
 * Creates new journey with typed edge (SPAWNED_BY, AFFECTS, etc.) to current journey.
 *
 * Use case: Staff is in admission journey, resident falls, need to spawn
 * incident journey WITHOUT leaving admission context.
 */

import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  ThemeIcon,
  Button,
  Select,
  Textarea,
  Badge,
  Alert,
  SimpleGrid,
  Card,
} from '@mantine/core';
import {
  IconGitBranch,
  IconPlus,
  IconAlertTriangle,
  IconLink,
  IconArrowRight,
  IconClipboard,
  IconHeartbeat,
  IconRefresh,
  IconShield,
} from '@tabler/icons-react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// Types
// ============================================================================

export interface MidJourneySpawnModalProps {
  /** Whether modal is open */
  opened: boolean;
  /** Callback to close modal */
  onClose: () => void;
  /** Parent instance ID */
  parentInstanceId: string;
  /** Parent workflow name (for display) */
  parentWorkflowName: string;
  /** Subject name (for display) */
  subjectName: string;
  /** Tenant ID */
  tenantId: string;
  /** Callback when spawn succeeds */
  onSpawnSuccess?: (newInstanceId: string) => void;
}

type EdgeType = 'SPAWNED_BY' | 'AFFECTS' | 'BLOCKS' | 'REQUIRES';

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  defaultEdgeType: EdgeType;
}

// ============================================================================
// Workflow Templates
// ============================================================================

const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'wf-incident',
    name: 'Incident Report',
    description: 'Document and investigate an incident',
    icon: <IconAlertTriangle size={20} />,
    color: 'red',
    defaultEdgeType: 'AFFECTS',
  },
  {
    id: 'wf-reassessment',
    name: 'Care Level Reassessment',
    description: 'Reassess care needs based on new information',
    icon: <IconRefresh size={20} />,
    color: 'orange',
    defaultEdgeType: 'SPAWNED_BY',
  },
  {
    id: 'wf-medical-emergency',
    name: 'Medical Emergency',
    description: 'Emergency medical response workflow',
    icon: <IconHeartbeat size={20} />,
    color: 'red',
    defaultEdgeType: 'BLOCKS',
  },
  {
    id: 'wf-compliance-review',
    name: 'Compliance Review',
    description: 'Regulatory compliance review triggered by event',
    icon: <IconShield size={20} />,
    color: 'violet',
    defaultEdgeType: 'AFFECTS',
  },
  {
    id: 'wf-documentation',
    name: 'Documentation Request',
    description: 'Request additional documentation',
    icon: <IconClipboard size={20} />,
    color: 'blue',
    defaultEdgeType: 'REQUIRES',
  },
];

const EDGE_TYPE_OPTIONS: Array<{ value: EdgeType; label: string; description: string }> = [
  {
    value: 'SPAWNED_BY',
    label: 'Spawned By',
    description: 'New journey was triggered by the parent journey',
  },
  {
    value: 'AFFECTS',
    label: 'Affects',
    description: 'New journey may impact tasks in the parent journey',
  },
  {
    value: 'BLOCKS',
    label: 'Blocks',
    description: 'Parent journey cannot complete until this resolves',
  },
  {
    value: 'REQUIRES',
    label: 'Requires',
    description: 'Parent journey needs output from this journey',
  },
];

// ============================================================================
// GraphQL Mutations
// ============================================================================

// Creates instance
const CREATE_RELATED_INSTANCE = gql`
  mutation CreateRelatedInstance($tenantId: String!, $input: GqlCreateInstanceInput!) {
    createInstance(tenantId: $tenantId, input: $input) {
      id
      status
      currentState
      workflowDefinitionId
    }
  }
`;

// DRR-309 Phase 5-7: Create typed edge between journey instances
const CREATE_JOURNEY_EDGE = gql`
  mutation CreateJourneyEdge($input: GqlCreateJourneyEdgeInput!) {
    createJourneyEdge(input: $input) {
      id
      sourceInstanceId
      targetInstanceId
      edgeType
      description
      createdAt
    }
  }
`;

// ============================================================================
// Main Component
// ============================================================================

export function MidJourneySpawnModal({
  opened,
  onClose,
  parentInstanceId,
  parentWorkflowName,
  subjectName,
  tenantId,
  onSpawnSuccess,
}: MidJourneySpawnModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [edgeType, setEdgeType] = useState<EdgeType>('SPAWNED_BY');
  const [description, setDescription] = useState('');
  const [step, setStep] = useState<'select' | 'configure'>('select');

  const [createInstance, { loading: createLoading, error: createError }] = useMutation(CREATE_RELATED_INSTANCE);
  const [createJourneyEdge, { loading: edgeLoading }] = useMutation(CREATE_JOURNEY_EDGE);

  const loading = createLoading || edgeLoading;
  const error = createError;

  const handleSelectTemplate = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setEdgeType(template.defaultEdgeType);
    setStep('configure');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedTemplate(null);
  };

  const handleSpawn = async () => {
    if (!selectedTemplate) return;

    try {
      // Step 1: Create the new instance
      const result = await createInstance({
        variables: {
          tenantId,
          input: {
            workflowDefinitionId: selectedTemplate.id,
            instanceName: `${selectedTemplate.name} — ${subjectName}`,
            inputData: {},
            contextVariables: {
              // Track relationship to parent journey (legacy, for backward compat)
              spawnedFrom: {
                parentInstanceId,
                parentWorkflowName,
                edgeType,
                description: description || null,
                spawnedAt: new Date().toISOString(),
              },
            },
          },
        },
      });

      const newInstanceId = result.data?.createInstance?.id;
      if (newInstanceId) {
        // Step 2: Create the typed edge (DRR-309 Phase 5-7)
        // Edge direction: new instance -> parent (e.g., "incident SPAWNED_BY admission")
        try {
          await createJourneyEdge({
            variables: {
              input: {
                sourceInstanceId: newInstanceId,
                targetInstanceId: parentInstanceId,
                edgeType,
                description: description || null,
              },
            },
          });
        } catch (edgeErr) {
          // Edge creation is non-critical — instance was created successfully
          console.warn('Failed to create journey edge (non-fatal):', edgeErr);
        }

        onSpawnSuccess?.(newInstanceId);
      }

      // Reset and close
      setSelectedTemplate(null);
      setStep('select');
      setDescription('');
      onClose();
    } catch (err) {
      console.error('Failed to spawn journey:', err);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setStep('select');
    setDescription('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <ThemeIcon color="violet" variant="light" size="lg">
            <IconGitBranch size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>Start Related Journey</Text>
            <Text size="xs" c="dimmed">
              {subjectName} — {parentWorkflowName}
            </Text>
          </div>
        </Group>
      }
      size="lg"
    >
      {step === 'select' ? (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select a journey type to start. It will be linked to the current journey.
          </Text>

          <SimpleGrid cols={2} spacing="sm">
            {WORKFLOW_TEMPLATES.map((template) => (
              <Card
                key={template.id}
                padding="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelectTemplate(template)}
              >
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon size="lg" variant="light" color={template.color}>
                    {template.icon}
                  </ThemeIcon>
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500}>
                      {template.name}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {template.description}
                    </Text>
                  </div>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      ) : (
        <Stack gap="md">
          {/* Selected template */}
          {selectedTemplate && (
            <Paper p="md" withBorder bg={`${selectedTemplate.color}.0`}>
              <Group justify="space-between">
                <Group gap="md">
                  <ThemeIcon size="lg" variant="light" color={selectedTemplate.color}>
                    {selectedTemplate.icon}
                  </ThemeIcon>
                  <div>
                    <Text fw={500}>{selectedTemplate.name}</Text>
                    <Text size="xs" c="dimmed">
                      {selectedTemplate.description}
                    </Text>
                  </div>
                </Group>
                <Button variant="subtle" size="xs" onClick={handleBack}>
                  Change
                </Button>
              </Group>
            </Paper>
          )}

          {/* Edge type relationship */}
          <div>
            <Text size="sm" fw={500} mb="xs">
              Relationship to Current Journey
            </Text>
            <Paper p="sm" withBorder>
              <Group gap="md" wrap="nowrap">
                <Badge variant="outline" color="gray">
                  {parentWorkflowName}
                </Badge>
                <ThemeIcon size="sm" variant="light" color="violet">
                  <IconLink size={12} />
                </ThemeIcon>
                <Select
                  value={edgeType}
                  onChange={(val) => setEdgeType(val as EdgeType)}
                  data={EDGE_TYPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  size="xs"
                  w={140}
                />
                <ThemeIcon size="sm" variant="light" color="violet">
                  <IconArrowRight size={12} />
                </ThemeIcon>
                <Badge variant="outline" color={selectedTemplate?.color ?? 'gray'}>
                  {selectedTemplate?.name}
                </Badge>
              </Group>
            </Paper>
            <Text size="xs" c="dimmed" mt="xs">
              {EDGE_TYPE_OPTIONS.find((o) => o.value === edgeType)?.description}
            </Text>
          </div>

          {/* Description */}
          <Textarea
            label="Reason / Notes"
            description="Explain why this related journey is being started"
            placeholder="e.g., Resident fell during room tour, need to document and investigate"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
          />

          {/* Warning for blocking relationship */}
          {edgeType === 'BLOCKS' && (
            <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
              <Text size="sm">
                <strong>Blocking relationship:</strong> The current journey will not be able
                to complete until this new journey is resolved.
              </Text>
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
              <Text size="sm">{error.message}</Text>
            </Alert>
          )}

          {/* Actions */}
          <Group justify="space-between">
            <Button variant="default" onClick={handleBack}>
              Back
            </Button>
            <Button
              color="violet"
              leftSection={<IconPlus size={16} />}
              onClick={handleSpawn}
              loading={loading}
            >
              Start Journey
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default MidJourneySpawnModal;
